// NOTE: Handle for creating estimate request body based on order details || not use store directly
import type { Path } from '@ahm/api-wrappers-core';
import { useMemo } from 'react';
import { SERVICE_HAVE_HUB_SP_TRANSFER_COD_REQUEST } from '@/lib/constants';

interface UseEstimateRequestBodyProps {
  orderDetail: any;
  updatedPaths?: Path[];
  serviceTypes?: any[];
}

const createRequestList = (
  requests: Record<string, any>,
  serviceId?: string
): { _id: string; num?: number; path_index?: any }[] => {
  if (!requests || !serviceId || Object.keys(requests).length === 0) return [];

  return Object.entries(requests)
    .filter(([key]) => key.includes(serviceId))
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((item) => ({
          _id: key,
          ...(key.includes('D2D') ? { path_index: item } : item),
        }));
      }

      if (value && typeof value === 'object') {
        return [{ _id: key, ...value }];
      }

      if (value) {
        return [
          {
            _id: key,
            ...(typeof value === 'number' ? { num: value } : {}),
          },
        ];
      }

      return [];
    });
};

export const useEstimateRequestBody = ({
  orderDetail,
  updatedPaths,
  serviceTypes = [],
}: UseEstimateRequestBodyProps) => {
  const estimateRequestBody = useMemo(() => {
    if (!orderDetail) {
      return null;
    }

    const {
      path,
      package_images,
      service_id,
      service,
      images,
      remarks,
      requests,
      package_detail,
      package_size,
      package_weight,
      item_descriptions,
      promo_code,
      payment_details,
    } = orderDetail;

    // Find the service from service types
    const selectedService =
      serviceTypes?.find((s) => s._id === service_id) || service;

    if (!selectedService) {
      return null;
    }

    const serviceRequests = transformServiceRequests(requests);

    const servicesParams = selectedService
      ? [
          {
            _id: selectedService._id,
            requests: createRequestList(serviceRequests, selectedService._id),
          },
        ]
      : [];

    const paymentPayloadData = {
      method_id: payment_details?.method_id,
      method_type: payment_details?.method_type,
      payment_method: orderDetail.payment_method,
    };

    const baseRequestBody = {
      service_id: selectedService._id,
      package_detail: package_detail || [],
      package_images: package_images || [],
      images: images || [],
      remarks: remarks || '',
      bike_package_size: package_size || 'S',
      bike_package_weight: package_weight,
      item_descriptions: item_descriptions || [],
      promo_code: promo_code,
      services: servicesParams,
      ...paymentPayloadData,
      ...(selectedService.delivery_type === 'TRUCK' &&
        service?.group_name_en_us && {
          truck_selected_group: service.group_name_en_us,
        }),
    };

    return {
      ...baseRequestBody,
      path: updatedPaths ?? path,
    };
  }, [orderDetail, updatedPaths, serviceTypes]);

  return {
    estimateRequestBody,
  };
};

const transformServiceRequests = (requests: any[]) => {
  if (!requests || requests.length === 0) return {};
  const serviceRequests = {};
  const D2DRequests = requests.filter((req) => req?._id.includes('D2D'));
  if (D2DRequests?.length) {
    serviceRequests[D2DRequests[0]?._id] = D2DRequests.map(
      (item) => item?.path_index
    );
  }
  const loadingGoodsRequests = requests.filter((req) =>
    req?._id.includes('LOADING-OPTIONS')
  );
  if (loadingGoodsRequests?.length) {
    serviceRequests[loadingGoodsRequests[0]?._id] = loadingGoodsRequests.map(
      (item) => {
        const result = { ...item };
        if (result?.options) {
          result.options = result.options.map((option: any) => {
            const newOption = { ...option };
            delete newOption.price;
            return newOption;
          });
        }
        delete result.price;
        return result;
      }
    );
  }
  requests.forEach((item: any) => {
    if (
      item?._id.includes('D2D') ||
      item?._id.includes('BULKY') ||
      item?._id.includes('LOADING-OPTIONS')
    )
      return;

    if (item?._id.includes('TRANSFER-COD')) {
      const serviceIdWithoutSuffix = item?._id.replace('-TRANSFER-COD', '');
      if (
        SERVICE_HAVE_HUB_SP_TRANSFER_COD_REQUEST.includes(
          serviceIdWithoutSuffix
        )
      ) {
        serviceRequests[item?._id] = true;
      }
      return;
    }

    if (item?._id.includes('FRAGILE')) {
      serviceRequests[item?._id] = {
        option_code: item.option_code,
        option_detail: item.option_detail,
      };
    } else {
      serviceRequests[item?._id] = item?.num || true;
    }
  });

  return serviceRequests;
};
